/**
 * Subject motifs: the imagery, drawn rather than fetched or generated.
 *
 * A photograph would need a licence to track and a source to trust; a generated image would
 * need somebody to check it is not quietly wrong. Neither is a good trade for decoration. So
 * the imagery here is abstract, owned, deterministic, and derived from the subject — a
 * molecular lattice behind chemistry, contour strata behind geography, a field of arcs behind
 * the arts.
 *
 * It is decoration and is drawn as such: low contrast, behind the content, `aria-hidden`, and
 * never carrying meaning. A slide reads exactly the same with the motif removed.
 *
 * Deterministic from a seed, so the same slide draws the same motif every time it is opened.
 * A deck that reshuffles its own decoration between reviews looks broken rather than alive.
 */

export type MotifKind = 'lattice' | 'grid' | 'strata' | 'arcs' | 'orbit' | 'field';

/** A tiny deterministic PRNG. Same seed, same motif, every render. */
function rng(seed: string) {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return () => {
    h ^= h << 13; h ^= h >>> 17; h ^= h << 5;
    return ((h >>> 0) % 100000) / 100000;
  };
}

/** Which motif a subject wears. Falls back to a neutral field rather than to nothing. */
export function motifFor(subject: string): MotifKind {
  const s = (subject ?? '').toLowerCase();
  if (/chem|physic|science|biolog/.test(s)) return 'lattice';
  if (/math|econom|business|comput|design/.test(s)) return 'grid';
  if (/geograph|histor|individual|societ|environment/.test(s)) return 'strata';
  if (/art|music|language|literature|drama|theatre/.test(s)) return 'arcs';
  if (/physical|health|sport/.test(s)) return 'orbit';
  return 'field';
}

export function Motif({
  kind,
  seed,
  opacity = 0.09,
}: {
  kind: MotifKind;
  seed: string;
  opacity?: number;
}) {
  const r = rng(seed);
  const stroke = 'var(--c-brand, #5b46d6)';
  const W = 600, H = 600;

  const shapes = (() => {
    switch (kind) {
      case 'lattice': {
        // A hexagonal-ish net of bonds, jittered so it never looks like graph paper.
        const nodes: [number, number][] = [];
        for (let y = 0; y < 6; y++) {
          for (let x = 0; x < 6; x++) {
            nodes.push([60 + x * 96 + (y % 2 ? 48 : 0) + r() * 14, 60 + y * 96 + r() * 14]);
          }
        }
        return (
          <>
            {nodes.map(([x, y], i) => {
              const next = nodes[i + 1];
              const below = nodes[i + 6];
              return (
                <g key={i}>
                  {next && (i % 6 !== 5) ? <line x1={x} y1={y} x2={next[0]} y2={next[1]} /> : null}
                  {below ? <line x1={x} y1={y} x2={below[0]} y2={below[1]} /> : null}
                  <circle cx={x} cy={y} r={4} fill={stroke} stroke="none" />
                </g>
              );
            })}
          </>
        );
      }
      case 'grid': {
        // A ruled field with one curve through it: the shape of every graph ever drawn.
        const path = `M0 ${420 + r() * 60} C ${W * 0.3} ${300 - r() * 80}, ${W * 0.6} ${240 + r() * 90}, ${W} ${120 + r() * 60}`;
        return (
          <>
            {Array.from({ length: 9 }, (_, i) => <line key={`h${i}`} x1={0} y1={i * 70} x2={W} y2={i * 70} />)}
            {Array.from({ length: 9 }, (_, i) => <line key={`v${i}`} x1={i * 70} y1={0} x2={i * 70} y2={H} />)}
            <path d={path} strokeWidth={5} />
          </>
        );
      }
      case 'strata': {
        // Contour lines, each one a softened copy of the one above it.
        return (
          <>
            {Array.from({ length: 10 }, (_, i) => {
              const y = 70 + i * 52;
              const a = 26 + r() * 34;
              return (
                <path
                  key={i}
                  d={`M-20 ${y} C ${W * 0.25} ${y - a}, ${W * 0.55} ${y + a}, ${W + 20} ${y - a / 2}`}
                />
              );
            })}
          </>
        );
      }
      case 'arcs': {
        const cx = 120 + r() * 80, cy = H - 80;
        return <>{Array.from({ length: 12 }, (_, i) => <circle key={i} cx={cx} cy={cy} r={60 + i * 52} />)}</>;
      }
      case 'orbit': {
        return (
          <>
            {Array.from({ length: 5 }, (_, i) => (
              <ellipse key={i} cx={W / 2} cy={H / 2} rx={90 + i * 55} ry={200 - i * 18} transform={`rotate(${i * 36 + r() * 20} ${W / 2} ${H / 2})`} />
            ))}
          </>
        );
      }
      default: {
        return (
          <>
            {Array.from({ length: 26 }, (_, i) => {
              const x = r() * W, y = r() * H;
              return <circle key={i} cx={x} cy={y} r={6 + r() * 34} />;
            })}
          </>
        );
      }
    }
  })();

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      width="100%"
      height="100%"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden="true"
      focusable="false"
      style={{ position: 'absolute', inset: 0, opacity, pointerEvents: 'none' }}
      fill="none"
      stroke={stroke}
      strokeWidth={2}
    >
      {shapes}
    </svg>
  );
}
